import { createPermissionPayload } from '@adornis/abac/policies/SimpleRolePolicy.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type ClientContext, context } from '@adornis/baseql/server/context.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { SmartTeamsCustomer } from '@nsc/smartteams-shared/db/SmartTeamsCustomer.js';
import { SmartTeamsAvailability } from '../db/SmartTeamsAvailability.js';
import { SmartTeamsPartnerCompany } from '../db/SmartTeamsCompany.js';
import { SmartTeamsFieldEngineer } from '../db/SmartTeamsFieldEngineer.js';
import { getPartnerCompanyFilter } from '../features/permissions/api/getCompanyFilter.js';
import { getFieldEngineerFilter } from '../features/permissions/api/getFieldEngineerFilter.js';
import { accessController } from '../features/permissions/init-permissions.js';
import { SmartTeamsPermission } from '../features/permissions/roles-and-permissions.js';

// ────────────────────────────────────────────────────────────────────────────
// Soft delete
//
// "Delete" in the back office is a soft delete: it stamps `deletedAt` with the current time
// instead of removing the document. Every list/picker query treats a document as live when
// `deletedAt == null` (which in Mongo matches both null and missing), so a stamped record simply
// disappears from the UI while the data is preserved.
//
// The generic ERM archive/delete ABAC is wired to allow-all in this app (see api/index.ts
// `initERM`), so the SmartTeams permission check below — plus the row-level scope check that
// reuses the same filters as the read queries — is the real authorization gate, not the client.
// ────────────────────────────────────────────────────────────────────────────

/** Stamp `deletedAt` on a single document. Throws if no document matched. */
const stampDeleted = async (collectionName: string, id: string): Promise<void> => {
  const col = await getRawCollection<{ _id: string; deletedAt?: Date | null }>(collectionName);
  const result = await col.updateOne({ _id: id }, { $set: { deletedAt: new Date() } });
  if (result.matchedCount !== 1) throw new Error('Record not found for deletion');
};

export const softDeletePartnerCompany = registerMutation({
  operationName: 'softDeletePartnerCompany',
  type: () => Boolean,
  params: [{ name: 'id', type: () => String }],
  resolve: (id: string) => async (): Promise<boolean> => {
    if (!(await accessController.can(createPermissionPayload(SmartTeamsPermission.CompanyDelete)))) {
      throw new Error('No permission to delete partner companies');
    }
    const filter = await getPartnerCompanyFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
      f.parseMongoFilter(),
    );
    const col = await getRawCollection<EntityData<SmartTeamsPartnerCompany>>(SmartTeamsPartnerCompany._collectionName);
    const existing = await col.findOne({ _id: id, ...filter }, { projection: { _id: 1 } });
    if (!existing) throw new Error('Partner company not found or access denied');

    await stampDeleted(SmartTeamsPartnerCompany._collectionName, id);
    return true;
  },
});

export const softDeleteCustomer = registerMutation({
  operationName: 'softDeleteCustomer',
  type: () => Boolean,
  params: [{ name: 'id', type: () => String }],
  resolve: (id: string) => async (): Promise<boolean> => {
    if (!(await accessController.can(createPermissionPayload(SmartTeamsPermission.CustomerDelete)))) {
      throw new Error('No permission to delete customers');
    }
    // Customers are not row-scoped (single tenant per install), so the permission check is enough.
    await stampDeleted(SmartTeamsCustomer._collectionName, id);
    return true;
  },
});

export const softDeleteFieldEngineer = registerMutation({
  operationName: 'softDeleteFieldEngineer',
  type: () => Boolean,
  params: [{ name: 'id', type: () => String }],
  resolve: (id: string) => async (): Promise<boolean> => {
    if (!(await accessController.can(createPermissionPayload(SmartTeamsPermission.FEDelete)))) {
      throw new Error('No permission to delete field engineers');
    }
    const filter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
      f.parseMongoFilter(),
    );
    const col = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(SmartTeamsFieldEngineer._collectionName);
    const existing = await col.findOne({ _id: id, ...filter }, { projection: { _id: 1 } });
    if (!existing) throw new Error('Field engineer not found or access denied');

    // Only the field engineer is soft-deleted; the linked login is intentionally left live so it
    // can be reused if the FE is re-created with the same email (see create-field-engineer-with-user).
    await stampDeleted(SmartTeamsFieldEngineer._collectionName, id);
    return true;
  },
});

export const softDeleteAvailability = registerMutation({
  operationName: 'softDeleteAvailability',
  type: () => Boolean,
  params: [{ name: 'id', type: () => String }],
  resolve: (id: string) => async (): Promise<boolean> => {
    if (!(await accessController.can(createPermissionPayload(SmartTeamsPermission.AvailabilityDelete)))) {
      throw new Error('No permission to delete availabilities');
    }
    const avCol = await getRawCollection<EntityData<SmartTeamsAvailability>>(SmartTeamsAvailability._collectionName);
    const availability = await avCol.findOne({ _id: id }, { projection: { _id: 1, fieldEngineerId: 1 } });
    if (!availability) throw new Error('Availability not found');

    // Availabilities inherit their scope from the field engineer they belong to.
    const feFilter = await getFieldEngineerFilter()({ allowedContexts: 1, hasAccess: 1, isGlobal: 1 }).then(f =>
      f.parseMongoFilter(),
    );
    const feCol = await getRawCollection<EntityData<SmartTeamsFieldEngineer>>(SmartTeamsFieldEngineer._collectionName);
    const fe = await feCol.findOne({ _id: availability.fieldEngineerId, ...feFilter }, { projection: { _id: 1 } });
    if (!fe) throw new Error('Availability not found or access denied');

    await stampDeleted(SmartTeamsAvailability._collectionName, id);
    return true;
  },
});

export const softDeleteUser = registerMutation({
  operationName: 'softDeleteUser',
  type: () => Boolean,
  params: [{ name: 'id', type: () => String }],
  resolve: (id: string) => async (): Promise<boolean> => {
    if (!(await accessController.can(createPermissionPayload(SmartTeamsPermission.UserManage)))) {
      throw new Error('No permission to delete users');
    }
    // Never let an admin delete the account they're currently signed in with.
    if ((context as ClientContext).userID === id) {
      throw new Error('You cannot delete your own account');
    }
    await stampDeleted(AdornisUser._collectionName, id);
    return true;
  },
});
